import { Injectable, Inject } from 'ng-metadata/core';
import { loadCSS } from './scriptLoader';
import { StorageUtil } from './infra/storageUtil.service';
import {TemplateUtil} from "./nddUtils/templateUtil.service";
import {ConfigurationUtil} from "./nddUtils/configurationUtil.service";
import {JwtUtilService} from "./security/jwtUtil.service";
import {UserSessionManagerService} from "./security/userSessionManager.service";
import {SharedSessionStorage} from "./infra/sharedSessionStorage.service";

const { once, clone,get } = require("lodash");

const STORAGE_KEYS = {
	ENABLED: "cmIntegrationEnabled",
	STYLSHEET: "cmIntegrationStylesheetUrl"
}

const MESSAGES = {
	NAVIGATE: "NAVIGATE",
	MAIN_NAVS: "MAIN_NAVS",
	OTHER_NAVS: "OTHER_NAVS",
	REQUEST_STYLESHEET: "REQUEST_STYLESHEET",
	LOAD_STYLESHEET: "LOAD_STYLESHEET",
	READY: "READY",
	WINDOWPOP: "WINDOWPOP",
	SEND_TOKEN: "SEND_TOKEN"
}

@Injectable()
export class CMIntegrationService {

	private enabled: boolean = false;
	private menu: Map<string, any> = new Map();
	
	private performAction: Function;

	private messageHandlers = {
		[MESSAGES.NAVIGATE]: (data: any) => {
			this.navItemAction(this.menu[data.code]);
		},
		[MESSAGES.LOAD_STYLESHEET]: (data: {url: string}) => {
			this.loadIntegrationStylesheet(data.url).then(() => this.communicate({name: MESSAGES.READY}));
		},
		[MESSAGES.SEND_TOKEN]: (data: any) => {
			this.userSessionManagerService.syncJwtWithTabs(data.code);
			this.jwtUtilService.storeJwt(data.code);
		}
	}

	constructor(@Inject('$state') private $state: ng.ui.IStateService,
		@Inject('$translate') private $translate: ng.translate.ITranslateService,
		@Inject('$window') private $window: ng.IWindowService,
		@Inject('$location') private $location: ng.ILocationService,
		private jwtUtilService: JwtUtilService,
		private userSessionManagerService: UserSessionManagerService,
		private storageUtil: StorageUtil,
		private sharedSessionStorage: SharedSessionStorage,
		private configurationUtil: ConfigurationUtil,
		private templateUtil: TemplateUtil) {
			this.enabled = this.checkEnabled();
			if (this.isEnabled()) {
				this.registerMessageListener();
				this.loadIntegrationStylesheet();
				let _this = this;
				_this.$window.onclick = function (e) {
					if (e.target.localName == 'a') {
						if(_this.isExternalLink(e)){
							_this.communicate({name: MESSAGES.WINDOWPOP , 'originalParams': [e.target.attributes['href'].nodeValue]})
						}

					}
				}

				//overriding the window.open since cm wraps primo in an
				// iframe in a webview - we want to message  cm to open the window
				this.wrap(window, "open", function(orginalFn){
					var originalParams = Array.prototype.slice.call(arguments, 1);
					originalParams = _this.supportRelativePathsUrl(originalParams);
					if(get(originalParams, '[0]').length === 0) {
						function cmPop(originalParams){
							this.paramsOfWin = originalParams;
							Object.defineProperty(this,"location", {
								set: function(urlTarget){
									if(get(this.paramsOfWin, '[0]') !== 'undefined'){
										this.paramsOfWin[0] = urlTarget;
										this.paramsOfWin = _this.supportRelativePathsUrl(this.paramsOfWin);
										_this.communicate({name: MESSAGES.WINDOWPOP , 'originalParams': this.paramsOfWin})
									}
								}
							});
						}
						return new cmPop(originalParams);
					}else{
						_this.communicate({name: MESSAGES.WINDOWPOP , 'originalParams': originalParams})
					}

				});
			}
	}

	isExternalLink(e){
		return e && e.target && e.target.attributes['href'] && e.target.attributes['href'].nodeValue && e.target.attributes['href'].nodeValue.indexOf('http') === 0 && !e.target.attributes['href'].nodeValue.startsWith(this.templateUtil.getBaseUrl());
	}

	supportRelativePathsUrl(originalParams){
		if(get(originalParams, '[0]').indexOf('/') === 0){
			let base = this.templateUtil.getBaseUrl()
			originalParams[0] = base + originalParams[0];
		}
		return originalParams
	}
	isEnabled() {
		return this.enabled;
	}
	
	ready() {
		this.communicate({name: MESSAGES.REQUEST_STYLESHEET});
	}

	registerMainNavs = once((menu: { code: string; label: string; action: any; href?: any; }[]) => {
		this.registerNav(MESSAGES.MAIN_NAVS, menu, menu.map((m: any) => m.label), (m: any) => this.$translate.instant(m.label));
	})
	
    registerNavMenu = once((menu: any, getLabel: any, performAction: any) => {
		this.performAction = performAction;
		this.registerNav(MESSAGES.OTHER_NAVS, menu, menu.map((m: any) => getLabel(m)), (el: any) => this.$translate.instant(getLabel(el)));
	})

	private overrideWindowOpen () {
		// TODO: Might need some handling when opening links in new window
	}

	private checkEnabled() {
		// TODO: Consider cases where a link is opened in a new window
		// const hasParent = this.$window.parent && this.$window.parent !== this.$window.window;
		// if (!hasParent) return false;
		const campusm = this.$location.search()["campusm"];
		const hasFlag = typeof campusm !== "undefined";
		const flagTrue = campusm === "true";
		const enabled = flagTrue || (!hasFlag && (this.sharedSessionStorage.getItem(STORAGE_KEYS.ENABLED) === "true"));
		this.sharedSessionStorage.setItem(STORAGE_KEYS.ENABLED, enabled.toString());
		return enabled;
	}
	
	private loadIntegrationStylesheet(stylesheetUrl: string = this.$location.search()["stylesheetUrl"]) {
		if (stylesheetUrl) {
			this.sharedSessionStorage.setItem(STORAGE_KEYS.STYLSHEET, stylesheetUrl);
		} else {
			stylesheetUrl = this.sharedSessionStorage.getItem(STORAGE_KEYS.STYLSHEET);
		}
		return stylesheetUrl ? loadCSS(stylesheetUrl) : Promise.resolve();
	}

	private communicate(message: any) {
		if (this.isEnabled()) {
			this.$window.parent.postMessage(message, "*");
		}
	}

	private registerNav(name: string, menu: any, labels: string[], translate: Function, performAction?: Function) {
		const list = [];
		this.$translate(labels).then(() => {
			menu.forEach((m: {code?: string, label: string, action: any, href?: any}) => {
				const cloned = clone(m);
				if (performAction) { cloned.performAction = true; }
				const code = cloned.code || cloned.label;
				this.menu[code] = cloned;
				list.push({code, label: translate(m), href: m.href});
			});
			this.communicate({name, data: list});
		});
	}

	private navItemAction(item: {action: any, sref?: any, performAction?: boolean}) {
		if (!item) return;
		if (this.performAction && item.performAction) {
			this.performAction(item);
		} else {
			item.action && item.action();
			if (item.sref) {
				const {state, params, opts} = item.sref;
				this.$state.go(state, params, opts);
			}
		}
	}

	private registerMessageListener() {
		this.$window.addEventListener("message", (message: MessageEvent) => {
			const f = this.messageHandlers[message.data.name];
			if (f && typeof f === "function") {
				message.stopImmediatePropagation();
				f(message.data);
				return false;
			}
			return true;
		});
	}

	private wrap(object, method, wrapper){
		var fn = object[method];

		return object[method] = function(){
			return wrapper.apply(this, [fn.bind(this)].concat(
				Array.prototype.slice.call(arguments)));
		};
	};

//You may want to 'unwrap' the method later
//(setting the method back to the original)
	private unwrap(object, method, orginalFn){
		object[method] = orginalFn;
	};






}




// WEBPACK FOOTER //
// ./src/main/webapp/components/cm_integration.service.ts